import React, { useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Favorite, FavoriteBorder, LocalMallOutlined } from "@mui/icons-material";

const MainBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  text-align: left;
  background-color: transparent;
  width: 100%;
  cursor: pointer;
  position: relative;
  transition: transform 0.2s ease;

  &:hover {
    img {
      transform: scale(1.04);
    }
  }

  @media only screen and (max-width: 949px) {
    width: 100%;
  }
`;

const ImgBox = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 1.2rem;
  width: 100%;
  aspect-ratio: 3 / 4;
  background-color: #f4f4f4;

  img {
    transition: transform 0.4s ease;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    border-radius: 1.2rem;
  }
`;

const TopRightWishlist = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;
  background-color: white;
  border: none;
  border-radius: 50%;
  width: 3.8rem;
  height: 3.8rem;
  min-width: 3.8rem;
  min-height: 3.8rem;
  aspect-ratio: 1 / 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0.3rem 0.8rem rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.2s ease;
  color: ${props => (props.isWishlisted ? "#ff3f6c" : "#222")};

  &:hover {
    transform: scale(1.1);
    background-color: #ffffff;
    color: ${props => (props.isWishlisted ? "#e0204d" : "#ff3f6c")};
  }

  svg {
    font-size: 2.1rem;
  }
`;

const BottomRightCart = styled.button`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  z-index: 10;
  background-color: white;
  border: none;
  border-radius: 50%;
  width: 3.8rem;
  height: 3.8rem;
  min-width: 3.8rem;
  min-height: 3.8rem;
  aspect-ratio: 1 / 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0.3rem 0.8rem rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.2s ease;
  color: #222;

  &:hover {
    transform: scale(1.1);
    background-color: #ffffff;
    color: #511d00;
  }

  svg {
    font-size: 2.1rem;
  }
`;

const MessageOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${props =>
    props.type === "error" ? "rgba(215, 32, 32, 0.95)" : "rgba(30, 30, 30, 0.95)"};
  color: white;
  padding: 0.8rem;
  font-size: 1.2rem;
  font-weight: 500;
  text-align: center;
  z-index: 12;
  transition: all 0.3s ease;
  transform: ${props => (props.show ? "translateY(0)" : "translateY(100%)")};
  border-bottom-left-radius: 1.2rem;
  border-bottom-right-radius: 1.2rem;
`;

const TextBox = styled.div`
  color: #222;
  text-align: left;
  width: 100%;
  padding-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  h3 {
    font-size: 1.4rem;
    font-weight: 500;
    line-height: 1.3;
    color: #222;
    margin: 0;
    text-transform: capitalize;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .price {
    font-size: 1.5rem;
    font-weight: 700;
    color: #111;
    margin: 0.1rem 0;
  }
`;

const SizeRow = styled.div`
  display: flex;
  gap: 0.7rem;
  align-items: center;
  font-size: 1.15rem;
  color: #666;
  font-weight: 500;
  letter-spacing: 0.04rem;
  margin-top: 0.1rem;
  flex-wrap: wrap;

  span {
    margin: 0 !important;
    padding: 0 !important;
  }
`;

const ColorRow = styled.div`
  display: flex;
  gap: 0.6rem;
  align-items: center;
  margin-top: 0.3rem;
`;

const ColorDot = styled.span`
  width: 1.4rem;
  height: 1.4rem;
  min-width: 1.4rem;
  min-height: 1.4rem;
  aspect-ratio: 1 / 1;
  flex-shrink: 0;
  border-radius: 50%;
  background-color: ${props => props.colorHex};
  border: 1px solid rgba(0, 0, 0, 0.2);
  display: inline-block;
  margin: 0 !important;
  padding: 0 !important;
  box-sizing: border-box;
`;

const getColorHex = (name) => {
  if (!name) return "#888888";
  const clean = name.trim().toLowerCase();
  const colorMap = {
    wine: "#581825",
    burgundy: "#800020",
    black: "#111111",
    red: "#d32f2f",
    blue: "#1976d2",
    navy: "#0a192f",
    green: "#2e7d32",
    pink: "#e91e63",
    yellow: "#fbc02d",
    maroon: "#800000",
    beige: "#d7c4b7",
    brown: "#795548",
    white: "#ffffff",
    grey: "#888888",
    gray: "#888888",
    purple: "#7b1fa2",
    orange: "#f57c00",
    teal: "#00796b"
  };
  return colorMap[clean] || (clean.startsWith("#") ? clean : "#666666");
};

const getSizesList = (stockData, hasSizes = true, sizeType = "apparel") => {
  if (hasSizes === false || hasSizes === "false") {
    return ["Standard"];
  }
  const defaultSizes = ["XS", "S", "M", "L", "XL", "2XL"];
  if (!stockData) return defaultSizes;
  let stockArr = stockData;
  if (typeof stockData === "string") {
    try {
      stockArr = JSON.parse(stockData);
    } catch (e) {
      return defaultSizes;
    }
  }
  if (Array.isArray(stockArr)) {
    const list = [];
    stockArr.forEach((item) => {
      if (typeof item === "object" && item !== null) {
        Object.keys(item).forEach((k) => {
          const isNum = !isNaN(Number(k));
          const label = isNum ? `UK ${k}` : k.toUpperCase();
          if (!list.includes(label)) list.push(label);
        });
      } else if (typeof item === "string") {
        const isNum = !isNaN(Number(item));
        const label = isNum ? `UK ${item}` : item.toUpperCase();
        if (!list.includes(label)) list.push(label);
      }
    });
    return list.length > 0 ? list : defaultSizes;
  } else if (typeof stockArr === "object" && stockArr !== null) {
    return Object.keys(stockArr).map((k) => (!isNaN(Number(k)) ? `UK ${k}` : k.toUpperCase()));
  }
  return defaultSizes;
};

const FeatureProductBox = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const isLoggedIn = useSelector((state) => state.isLoggedIn);
  const userId = useSelector((state) => state.userId);
  const userToken = useSelector((state) => state.userToken);
  const wishlistItems = useSelector((state) => state.wishlistItems) || [];
  const cartItems = useSelector((state) => state.cartItems) || [];

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");

  if (!props.data) return null;

  const { title, img, price, id, stock, color, colors: propColors, badge, hasSizes, sizeType } = props.data;

  const isWishlisted = wishlistItems.some((item) => item.id === id);

  const showBoxMessage = (text, type = "info") => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
    }, 3000);
  };

  const getAvailableSize = (stockArray) => {
    if (hasSizes === false || hasSizes === "false") return "Standard";
    if (!stockArray) return sizeType === "footwear" ? "5" : "M";
    let stockArr = stockArray;
    if (typeof stockArray === "string") {
      try {
        stockArr = JSON.parse(stockArray);
      } catch (e) {
        return sizeType === "footwear" ? "5" : "M";
      }
    }
    if (!Array.isArray(stockArr)) return sizeType === "footwear" ? "5" : "M";
    for (const sizeObj of stockArr) {
      for (const size in sizeObj) {
        if (Number(sizeObj[size]) > 0) {
          return size;
        }
      }
    }
    return sizeType === "footwear" ? "5" : "M";
  };

  const toggleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isLoggedIn) {
      navigate("/login");
      return;
    }

    try {
      if (isWishlisted) {
        const res = await fetch(`${process.env.REACT_APP_BASE_URL}/user/wishlist/remove`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
          body: JSON.stringify({ userId, productId: id }),
        });
        const data = await res.json();
        if (data.success) {
          dispatch({ type: "removeFromWishlist", productId: id });
          showBoxMessage("Removed from wishlist", "info");
        } else {
          showBoxMessage(data.message || "Failed to update wishlist", "error");
        }
      } else {
        const res = await fetch(`${process.env.REACT_APP_BASE_URL}/user/wishlist/add`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
          body: JSON.stringify({ userId, productId: id }),
        });
        const data = await res.json();
        if (data.success) {
          dispatch({ type: "addToWishlist", product: props.data });
          showBoxMessage("Added to wishlist!", "success");
        } else {
          showBoxMessage(data.message || "Failed to update wishlist", "error");
        }
      }
    } catch (err) {
      console.error("Wishlist error:", err);
      showBoxMessage("Something went wrong.", "error");
    }
  };

  const addToCartDirect = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    const availableSize = getAvailableSize(stock);
    const firstColor = color ? color.split(",")[0].trim() : "Default";

    const titleArr = title ? title.split(" ") : ["Product"];

    const obj = {
      title: titleArr[1] ? titleArr[0] + " " + titleArr[1] : titleArr[0],
      productId: id,
      price: price,
      image: img,
      quantity: 1,
      color: firstColor,
      size: availableSize,
      category: props.data.category || "",
      slug: props.data.slug || "",
      stockAvailable: 10,
    };

    const alreadyFound = cartItems.find(
      (item) => item.productId === obj.productId && item.size === obj.size
    );
    const quantityInCart = alreadyFound ? alreadyFound.quantity : 0;

    if (quantityInCart + 1 > 2) {
      showBoxMessage("Max 2 units allowed per size", "error");
      return;
    }

    try {
      const stockVerifier = await fetch(
        `${process.env.REACT_APP_BASE_URL}/product/stock`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            productId: id,
            quantity: quantityInCart + 1,
            size: availableSize,
          }),
        }
      );
      const stockData = await stockVerifier.json();
      if (stockData.add) {
        dispatch({ type: "addToCart", product: obj });
        showBoxMessage("Added to cart!", "success");
      } else {
        showBoxMessage("Selected size is out of stock!", "error");
      }
    } catch (err) {
      console.error("Add to cart error:", err);
      // Fallback local add if server endpoint has issues
      dispatch({ type: "addToCart", product: obj });
      showBoxMessage("Added to cart!", "success");
    }
  };

  const sizesList = getSizesList(stock, hasSizes, sizeType);

  let rawColors = [];
  if (Array.isArray(propColors) && propColors.length > 0) {
    rawColors = propColors;
  } else if (typeof color === "string" && color.trim().length > 0) {
    rawColors = color.split(",");
  } else {
    rawColors = ["wine", "black"];
  }

  return (
    <MainBox>
      <ImgBox>
        <img src={img} alt={title || "Product"} />

        <TopRightWishlist
          isWishlisted={isWishlisted}
          onClick={toggleWishlist}
          title="Add to Wishlist"
        >
          {isWishlisted ? <Favorite /> : <FavoriteBorder />}
        </TopRightWishlist>

        <BottomRightCart onClick={addToCartDirect} title="Add to Cart">
          <LocalMallOutlined />
        </BottomRightCart>

        <MessageOverlay show={!!message} type={messageType}>
          {message}
        </MessageOverlay>
      </ImgBox>

      <TextBox>
        <h3>{title}</h3>
        <div className="price">₹{Number(price).toLocaleString("en-IN")}</div>

        <SizeRow>
          {sizesList.map((sz, idx) => (
            <span key={idx}>{sz}</span>
          ))}
        </SizeRow>

        <ColorRow>
          {rawColors.slice(0, 4).map((c, idx) => (
            <ColorDot key={idx} colorHex={getColorHex(c)} title={c.trim()} />
          ))}
        </ColorRow>
      </TextBox>
    </MainBox>
  );
};

export default FeatureProductBox;
